import type { CustomerIntegration } from '@/lib/models/customerIntegration';
import { CustomerIntegrationSchema } from '@/lib/models/customerIntegration';
import customerEndpoints from '@/lib/api/endpoints/customerEndpoints';
import type { Customer } from '@/lib/models/customer';
import { CustomerSchema } from '@/lib/models/customer';
import type { ODataResponse } from '@/lib/models/common';
import { zodValidator } from '@/lib/utils/zodValidator';

/**
 * Interfaccia per il service dei Customers
 * Mantiene esattamente gli stessi nomi dei metodi del JavaScript originale
 * Wrapper che estrae automaticamente i dati dalle response Fetch
 */
export interface CustomersService {
	/**
	 * Recupera la lista dei clienti con query OData
	 * @param query - Query string OData
	 * @returns Promise con response OData contenente array di Customer
	 */
	getCustomers(query: string): Promise<ODataResponse<Customer>>;

	/**
	 * Recupera un singolo cliente per ID
	 * @param id - ID del cliente
	 * @returns Promise con singolo Customer
	 */
	getCustomer(id: number): Promise<Customer>;

	/**
	 * Aggiorna un cliente esistente
	 * @param id - ID del cliente da aggiornare
	 * @param payload - Dati da aggiornare
	 * @returns Promise con Customer aggiornato
	 */
	updateCustomer(id: number, payload: Customer): Promise<Customer>;

	/**
	 * Crea un nuovo cliente
	 * @param payload - Dati del nuovo cliente
	 * @returns Promise con Customer creato
	 */
	postCustomer(payload: Customer): Promise<Customer>;

	/**
	 * Elimina un cliente
	 * @param id - ID del cliente da eliminare
	 * @returns Promise void
	 */
	deleteCustomer(id: number): Promise<void>;

	/**
	 * Recupera le integrazioni di un cliente
	 * @param customerId - ID del cliente
	 * @returns Promise con array di CustomerIntegration (con expand)
	 */
	getIntegrations(customerId: number): Promise<CustomerIntegration[]>;

	/**
	 * Genera file Excel per i clienti
	 * @param query - Query string per i filtri
	 * @returns Promise con blob del file Excel
	 */
	generateExcel(query: string): Promise<Blob>;
}

/**
 * Implementazione del service per i Customers
 * Mantiene esattamente gli stessi nomi dei metodi del JavaScript originale
 * Estrae automaticamente la proprietà .data dalle response Fetch
 */
const customersService: CustomersService = {
	async getCustomers(query: string): Promise<ODataResponse<Customer>> {
		const response = (await customerEndpoints.getCustomers(query)).data;
		// Validazione runtime centralizzata (attiva solo in development)
		zodValidator.validateODataResponse(response, CustomerSchema, 'Customer.getAll');
		return response;
	},

	async getCustomer(id: number): Promise<Customer> {
		const rawData = (await customerEndpoints.getCustomer(id)).data;
		// Validazione runtime centralizzata (attiva solo in development)
		zodValidator.validateSingle(rawData, CustomerSchema, `Customer.getById(${id})`);
		return rawData;
	},

	async updateCustomer(id: number, payload: Customer): Promise<Customer> {
		const rawData = (await customerEndpoints.updateCustomer(id, payload)).data;
		// Validazione runtime centralizzata (attiva solo in development)
		zodValidator.validateSingle(rawData, CustomerSchema, `Customer.updateById(${id})`);
		return rawData;
	},

	async postCustomer(payload: Customer): Promise<Customer> {
		const rawData = (await customerEndpoints.postCustomer(payload)).data;
		// Validazione runtime centralizzata (attiva solo in development)
		zodValidator.validateSingle(rawData, CustomerSchema, 'Customer.create');
		return rawData;
	},

	async deleteCustomer(id: number): Promise<void> {
		return (await customerEndpoints.deleteCustomer(id)).data;
	},

	async getIntegrations(customerId: number): Promise<CustomerIntegration[]> {
		const { data } = await customerEndpoints.getIntegrations(customerId);
		// Validazione runtime centralizzata per le integrazioni (attiva solo in development)
		zodValidator.validateODataResponse(
			data,
			CustomerIntegrationSchema,
			`Customer.getIntegrations(${customerId})`,
		);
		return data.value;
	},

	async generateExcel(query: string): Promise<Blob> {
		return (await customerEndpoints.generateExcel(query)).data;
	},
};

export default customersService;
